import dayjs from 'dayjs';

/**
 * Derives the default "Valid until" date for retrospective PCFs: one year after the
 * base date, minus one day, so the validity period covers exactly one year.
 *
 * @param date - The base date.
 * @returns A new Date one year minus one day after the input. e.g. 15.03.2027 -> 14.03.2028
 */
export const defaultValidityPeriodEnd = (date: Date): Date => {
  return dayjs(date).add(1, 'year').subtract(1, 'day').toDate();
};

/**
 * Derives the end of a prospective reference period from its start date:
 * one year after the start, minus one day.
 *
 * @param start - The (planned) production start date.
 * @returns The reference period end date. e.g. 15.03.2027 -> 14.03.2028
 */
export const endOfReferencePeriod = (start: Date): Date => {
  return dayjs(start).add(1, 'year').subtract(1, 'day').toDate();
};

/**
 * Expands a picked year into the full calendar year it covers.
 *
 * Used for retrospective reference periods, where a single year click stands for the
 * whole period.
 *
 * @param selected - Any date within the selected year.
 * @returns The reference period as [start, end]. e.g. 2025 -> [01.01.2025, 31.12.2025]
 */
export const fullYearReferencePeriod = (selected: Date): Date[] => {
  return [
    dayjs(selected).startOf('year').toDate(),
    dayjs(selected).endOf('year').startOf('day').toDate(),
  ];
};

/**
 * The earliest date that counts as "in the future" (start of tomorrow).
 *
 * Used to validate that the validity period end lies in the future, even when a
 * computed default (e.g. dateCreated + 1 year) would fall in the past.
 *
 * @returns Start of the next day.
 */
export const startOfTomorrow = (): Date => {
  return dayjs().add(1, 'day').startOf('day').toDate();
};
